import { IPartNote, mapNotesToManagedNotes } from 'bom/components';
import { BomPartViewByType } from 'bom/enums';
import { usePartNotes } from 'bom/hooks';
import { useFormContext, useNotificationContext } from 'core/states';
import { INote } from 'core/types';
import { useJobPartNoteActions } from 'jobs/hooks';

interface IProps {
  jobId: number;
  componentId: number;
  notes: INote[];
  setNotes: (value: INote[]) => void;
  isEdit?: boolean;
}

export const useNotes = ({ jobId, componentId, notes = [], setNotes, isEdit = false }: IProps) => {
  const { clearNotification } = useNotificationContext();
  const { getValues, setValue } = useFormContext();

  const { removeJobPartNotes, upsertJobPartNote } = useJobPartNoteActions();

  const onAddPartNote = () => async (data: IPartNote) => {
    if (isEdit) {
      const { success, componentNote: updatedNote } = await upsertJobPartNote({
        bomId: jobId,
        bomPartViewBy: {
          group: BomPartViewByType.PART,
        },
        componentNote: {
          content: data.content,
          componentId,
        },
      });

      if (success) {
        setTimeout(() => {
          handleOnSubmitSuccess(updatedNote);
          clearNotification();
        }, 1000);
      }
    }
  };

  const onEditPartNote = async (data: Partial<INote>) => {
    const { success, componentNote: updatedNote } = await upsertJobPartNote({
      bomId: jobId,
      bomPartViewBy: {
        group: BomPartViewByType.PART,
      },
      componentNote: {
        content: data.content,
        id: data.id,
      },
    });

    if (success) {
      setTimeout(() => {
        handleOnSubmitSuccess(updatedNote);
        clearNotification();
      }, 1000);
    }

    return Promise.resolve({ success });
  };

  const onRemovePartNote = async (id: number) => {
    const { success } = await removeJobPartNotes({
      bomId: jobId,
      bomPartViewBy: {
        group: BomPartViewByType.PART,
      },
      componentId,
      ids: [id],
    });

    if (success) {
      handleOnSubmitSuccess(undefined, id);
    }
  };

  const handleOnSubmitSuccess = (note?: IPartNote, deleteNoteId?: number) => {
    const prevNotes = getValues('notes');

    const updatedNotesObject: Record<number, IPartNote> = {};

    prevNotes.forEach((noteItem: IPartNote) => {
      updatedNotesObject[noteItem.id] = noteItem;
    });

    if (deleteNoteId) {
      delete updatedNotesObject[deleteNoteId];
    }

    if (note) {
      updatedNotesObject[note.id] = note;
    }

    setValue?.('notes', mapNotesToManagedNotes(Object.values(updatedNotesObject)));
  };

  const { onAddNote, onDeleteNote, onEditNote } = usePartNotes({
    notes,
    setNotes,
    isEdit,
    onAddPartNote,
    onEditPartNote,
    onRemovePartNote,
  });

  return {
    onAddNote,
    onDeleteNote,
    onEditNote,
  };
};
